import React, { useState, useCallback, useRef, Fragment } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { getRegion } from "@util/domain";
import Card from "@components/cards/Card/Card";
import TextInput from "@components/inputs/TextInput/TextInput";
import Button from "@components/buttons/Button/Button";
import styles from "@styles/CommonAuthPageLayout.module.css";
import { isValidEmail, isValidPassword } from "@util/validators";
import { useAuth } from "@context/auth-context";
import { useBrandingOverlay } from "@context/branding-overlay-context";
import ReCAPTCHA, { ReCAPTCHAInterface } from "@components/misc/ReCAPTCHA/ReCAPTCHA";
import { validatePromoCode } from "@services/paymentService";

const INVALID_CREDENTIALS_MESSAGE = "Invalid email or password. Please try again.";

const SignInPage = () => {
    const navigate = useNavigate();
    const { login } = useAuth();
    const { show: showBrandingOverlay } = useBrandingOverlay();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [emailError, setEmailError] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [signInError, setSignInError] = useState("");
    const [loading, setLoading] = useState(false);

    const [captchaError, setCaptchaError] = useState<string | null>(null);
    const recaptchaRef = useRef<ReCAPTCHAInterface>(null);

    // Validation functions
    const validateEmail = useCallback((val: string) => {
        return isValidEmail(val);
    }, []);

    const validatePassword = useCallback((val: string) => {
        return isValidPassword(val);
    }, []);

    // Handlers with validation
    const onEmailChange = useCallback((val: string) => {
        setEmail(val);
        setEmailError(validateEmail(val));
    }, [validateEmail]);

    const onPasswordChange = useCallback((val: string) => {
        setPassword(val);
        setPasswordError(validatePassword(val));
    }, [validatePassword]);

    // Sign-Up Handler
    const handleSignIn = async () => {
        let isValid = true;

        // Validate Email
        const emailValidationError = validateEmail(email);
        setEmailError(emailValidationError);
        if (emailValidationError) isValid = false;

        // Validate Password
        const passwordValidationError = validatePassword(password);
        setPasswordError(passwordValidationError);
        if (passwordValidationError) isValid = false;

        // If both email and password are valid, proceed
        if (isValid) {
            setSignInError("");
            setCaptchaError("");

            const captchaToken = await recaptchaRef.current?.execute();
            if (!captchaToken) {
                setCaptchaError("reCAPTCHA verification failed.");
                return;
            }

            try {
                setLoading(true); // Show loading
                const user = await login(email, password, captchaToken);
                let destination = "";

                // Retrieve the stored URL and navigate to it if come from another page
                const redirectAfterSignIn = localStorage.getItem("redirectAfterSignIn");
                if (redirectAfterSignIn) {
                    localStorage.removeItem("redirectAfterSignIn");
                    destination = redirectAfterSignIn;
                } else if ((user.userType === "business_admin") && (user.subscription === "none")) {
                    const coupon = localStorage.getItem("coupon");
                    if (coupon) {
                        const result = await validatePromoCode(coupon);
                        if (result.isValid) {
                            destination = "/account/subscribe";
                        } else {
                            localStorage.removeItem("coupon");
                        }
                    }
                }

                if (!destination) {
                    switch (user.userType) {
                        case "root_admin": {
                            destination = "/businesses";
                            break;
                        }
                        case "demo":
                        case "business_admin":
                        case "business_user": {
                            destination = "/upcoming-services";
                            break;
                        }
                        case "standard_user":
                        default:
                            destination = "/memorials";
                    }
                }
                showBrandingOverlay({ onComplete: () => navigate(destination) });
            } catch (error) {
                // @ts-expect-error
                setSignInError(error.response ? error.response.data.message
                    : INVALID_CREDENTIALS_MESSAGE);
            } finally {
                setLoading(false); // Hide loading
            }
        } else {
            // Show the appropriate error message
            setSignInError(INVALID_CREDENTIALS_MESSAGE);
        }
    };

    // Enter moves to the next field, or submits from the last field once all are filled
    const focusField = (id: string) => document.getElementById(id)?.focus();

    const onEmailEnter = () => focusField("password");

    const onPasswordEnter = () => {
        if (!email) {
            focusField("email");
        } else if (password) {
            handleSignIn();
        }
    };

    return (
        <>
            <Helmet>
                <title>Sign In | Memorial Hub | Forever In Our Hearts</title>
                <meta
                    name="description"
                    content={`Sign in to manage your online memorials and tributes. Easily update, ${getRegion() === "global" ? "personalize" : "personalise"}, and share your loved one’s legacy pages with family and friends.`}
                />
            </Helmet>
            <div>
                <Card className={styles.cardContainer}>
                    <TextInput
                        label="Email"
                        id="email"
                        value={email}
                        onChange={onEmailChange}
                        placeholder="Enter email..."
                        errorMessage={emailError}
                        disabled={loading} // Disable while loading
                        onEnter={onEmailEnter}
                    />
                    <TextInput
                        label="Password"
                        id="password"
                        value={password}
                        onChange={onPasswordChange}
                        placeholder="Enter password..."
                        type="password"
                        errorMessage={passwordError}
                        disabled={loading} // Disable while loading
                        onEnter={onPasswordEnter}
                    />
                    <Button
                        variant="primary"
                        onClick={handleSignIn}
                        disabled={loading} // Disable button during loading
                    >
                        {loading ? "Signing In..." : "Sign In"}
                        {" "}
                        {/* Show loading text */}
                    </Button>
                    {signInError && (<p className={styles.errorMessage}>{signInError}</p>)}
                    {captchaError && <div className={styles.errorMessage}>{captchaError}</div>}

                    <div className={styles.linkContainer}>
                        <p>Don&apos;t have an account?</p>
                        <Link to="/auth/sign-up" className={styles.link}>
                            Sign Up
                        </Link>
                    </div>
                </Card>
                <div className={styles.linkContainer}>
                    <Link to="/auth/forgot-password" className={styles.link}>
                        Forgot Password
                    </Link>
                </div>
                <ReCAPTCHA ref={recaptchaRef} />
            </div>
        </>
    );
};

export default SignInPage;
